import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ChevronLeft, ChevronRight, Recycle } from 'lucide-react';
import type { BatchTicketRow } from '@autopilot/contracts';
import { api, type TicketListParams } from '../../api/client';
import { qk, useRoles } from '../../api/hooks';
import { isConflict } from '../../api/errors';
import { StatusBadge, ToneBadge } from '../../components/StatusBadge';
import { EmptyState, ErrorAlert, QueryBoundary } from '../../components/States';
import { ANALYSIS_STATE_LABEL, EXECUTION_STATE_LABEL, RESULT_CATEGORY_LABEL, VORGANG_STATUS_LABEL, reasonLabel } from '../../lib/labels';

export type TableFilter = NonNullable<TicketListParams['filter']>;
type SortKey = NonNullable<TicketListParams['sort']>;

export const FILTER_LABEL: Record<TableFilter, string> = {
  all: 'Alle',
  eligible: 'Ausführbar',
  blocked: 'Gesperrt',
  failed: 'Fehler/Konflikt',
  excluded: 'Ausgeschlossen',
  needs_review: 'Neue Prüfung erforderlich',
};

export function ScoreCell({ row, field }: { row: BatchTicketRow; field: 'correctness' | 'errorRisk' }) {
  const v = row[field];
  if (row.analysisState === 'EVALUATING' || row.analysisState === 'GENERATING' || row.analysisState === 'CONTEXT_LOADING') {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        wird geprüft
      </Typography>
    );
  }
  if (v === null) return <Typography variant="body2" sx={{ color: 'text.secondary' }}>–</Typography>;
  if (!row.scoresCurrent) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }} aria-label={`${v}, veraltet`}>
          {v}
        </Typography>
        <ToneBadge tone="warning" label="veraltet" size="small" />
      </Box>
    );
  }
  return (
    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
      {v}
    </Typography>
  );
}

export function ResultReason({ row }: { row: BatchTicketRow }) {
  if (row.executionState) {
    const e = EXECUTION_STATE_LABEL[row.executionState];
    return <ToneBadge tone={e.tone} label={e.label} wrap />;
  }
  if (row.eligible) return <ToneBadge tone="success" label="Erfüllt die Kriterien" wrap />;
  if (row.analysisState === 'NEEDS_REVIEW' || (!row.scoresCurrent && row.proposalVersion !== null)) {
    return <ToneBadge tone="warning" label="Neue Prüfung erforderlich" wrap />;
  }
  if (row.analysisState === 'FAILED') return <ToneBadge tone="error" label="Fehler bei der Verarbeitung" />;
  if (['PENDING', 'CONTEXT_LOADING', 'GENERATING', 'EVALUATING'].includes(row.analysisState)) {
    return <ToneBadge tone="info" label={ANALYSIS_STATE_LABEL[row.analysisState]} />;
  }
  const cat = RESULT_CATEGORY_LABEL[row.resultCategory];
  const text = row.ineligibleReason ? reasonLabel(row.ineligibleReason) : cat?.label ?? row.resultCategory;
  const blockers = row.blockers.length ? row.blockers.join(' · ') : null;
  const badge = <ToneBadge tone="neutral" label={text} showIcon wrap />;
  return blockers ? <Tooltip title={blockers}>{badge}</Tooltip> : badge;
}

function TruncCell({ text, fallback }: { text: string | null; fallback?: string }) {
  return (
    <TableCell>
      <Typography variant="body2" noWrap title={text ?? fallback} sx={{ fontSize: 'inherit', fontStyle: text ? 'normal' : 'italic', color: text ? 'text.primary' : 'text.secondary' }}>
        {text ?? fallback}
      </Typography>
    </TableCell>
  );
}

export function TicketTable({
  batchId,
  batchRowVersion,
  minCorrectness,
  maxErrorRisk,
  filter,
  resultCategory,
  onFilterChange,
  onOpen,
  canEditExclusion,
  live,
}: {
  batchId: string;
  batchRowVersion: number;
  minCorrectness: number;
  maxErrorRisk: number;
  filter: TableFilter;
  resultCategory?: string;
  onFilterChange: (f: TableFilter) => void;
  onOpen: (id: string) => void;
  canEditExclusion: boolean;
  live: boolean;
}) {
  const qc = useQueryClient();
  const { isOperator } = useRoles();
  const [sort, setSort] = useState<SortKey>('nummer');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [limit, setLimit] = useState(25);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const page = cursors.length - 1;
  const cursor = cursors[page];

  useEffect(() => setCursors([undefined]), [filter, resultCategory, sort, dir, limit, minCorrectness, maxErrorRisk]);

  const params: TicketListParams = { cursor, limit, filter, sort, dir, minCorrectness, maxErrorRisk, resultCategory };
  const q = useQuery({
    queryKey: [...qk.tickets(batchId, params), batchRowVersion],
    queryFn: () => api.listTickets(batchId, params),
    placeholderData: keepPreviousData,
    refetchInterval: live ? 4000 : false,
  });

  const exclusion = useMutation({
    mutationFn: (row: BatchTicketRow) => api.setExclusion(row.id, { excluded: !row.excluded, expectedRowVersion: row.rowVersion }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.batch(batchId) });
    },
    onError: (err) => {
      if (isConflict(err)) void qc.invalidateQueries({ queryKey: qk.batch(batchId) });
    },
  });

  const sortHeader = (key: SortKey, label: string, align: 'left' | 'right' = 'left') => (
    <TableCell align={align} sortDirection={sort === key ? dir : false}>
      <TableSortLabel
        active={sort === key}
        direction={sort === key ? dir : 'asc'}
        onClick={() => {
          if (sort === key) setDir(dir === 'asc' ? 'desc' : 'asc');
          else {
            setSort(key);
            setDir(key === 'correctness' ? 'desc' : 'asc');
          }
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const rows = q.data?.items ?? [];
  const from = page * limit + 1;

  return (
    <Card component="section" aria-labelledby="tickets-title" id="ticket-table">
      <Box sx={{ p: { xs: 2, md: 3 }, pb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography id="tickets-title" variant="h5" component="h2">
          Tickets
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_, v) => v && onFilterChange(v)}
          aria-label="Tickets filtern"
          sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.5 }, '& .Mui-selected': { color: 'primary.dark', backgroundColor: 'var(--color-primary-soft) !important' } }}
        >
          {(Object.keys(FILTER_LABEL) as TableFilter[]).map((f) => (
            <ToggleButton key={f} value={f} data-testid={`table-filter-${f}`}>
              {FILTER_LABEL[f]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      {resultCategory && (
        <Typography variant="body2" sx={{ px: 3, pb: 1, color: 'text.secondary' }}>
          Gefiltert nach Ergebnis: {RESULT_CATEGORY_LABEL[resultCategory as keyof typeof RESULT_CATEGORY_LABEL]?.label ?? resultCategory}
        </Typography>
      )}
      {exclusion.error ? (
        <Box sx={{ px: 3, pb: 1 }}>
          <ErrorAlert error={exclusion.error} action="Ausschluss ändern" onClose={() => exclusion.reset()} />
        </Box>
      ) : null}
      <QueryBoundary query={q} action="Tickets laden" loadingRows={8}>
        {rows.length === 0 ? (
          <EmptyState title="Keine Tickets in dieser Ansicht" description="Wählen Sie einen anderen Filter oder passen Sie die Kriterien an." />
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table
              size="small"
              aria-labelledby="tickets-title"
              sx={{ minWidth: 1100, tableLayout: 'fixed', '& td, & th': { px: 1, fontSize: '0.8125rem' }, '& td:first-of-type, & th:first-of-type': { pl: 2 } }}
            >
              <colgroup>
                <col style={{ width: 72 }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: 92 }} />
                <col style={{ width: 106 }} />
                <col />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Tooltip title="Im Batch berücksichtigen (abwählen = aus diesem Batch ausschließen)">
                      <span>Auswahl</span>
                    </Tooltip>
                  </TableCell>
                  {sortHeader('nummer', 'Vorgangsnummer')}
                  {sortHeader('gesellschaft', 'Gesellschaft')}
                  <TableCell>Liegenschaft</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell sx={{ whiteSpace: 'normal !important', lineHeight: 1.2 }}>Bisheriger Bearbeiter</TableCell>
                  {sortHeader('status', 'Status')}
                  {sortHeader('correctness', 'Richtigkeit', 'right')}
                  {sortHeader('errorRisk', 'Fehlerrisiko', 'right')}
                  <TableCell>Ergebnisgrund</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const hardExcluded = r.analysisState === 'EXCLUDED' && !r.excluded;
                  return (
                    <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => onOpen(r.id)} data-testid={`ticket-row-${r.nummer}`}>
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={!r.excluded && !hardExcluded}
                          disabled={!canEditExclusion || !isOperator || hardExcluded || !!r.executionState || exclusion.isPending}
                          onChange={() => exclusion.mutate(r)}
                          slotProps={{ input: { 'aria-label': `${r.nummer} ${r.excluded ? 'wieder in den Batch aufnehmen' : 'aus dem Batch ausschließen'}` } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpen(r.id);
                          }}
                          sx={{ fontWeight: 700, textAlign: 'left' }}
                        >
                          {r.nummer}
                        </Link>
                        {r.reused && (
                          <Tooltip title="Analyse aus früherem Batch wiederverwendet (keine neuen Tokens)">
                            <Box component="span" sx={{ ml: 0.75, color: 'text.secondary', verticalAlign: 'middle' }} aria-label="wiederverwendet">
                              <Recycle size={14} />
                            </Box>
                          </Tooltip>
                        )}
                        {r.betreff && (
                          <Typography variant="caption" component="div" noWrap sx={{ color: 'text.secondary', maxWidth: '100%' }} title={r.betreff}>
                            {r.betreff}
                          </Typography>
                        )}
                      </TableCell>
                      <TruncCell text={r.gesellschaftName ?? '–'} />
                      <TruncCell text={r.objektBezeichnung} fallback="Ohne Liegenschaft" />
                      <TruncCell text={r.vorgangstypName} fallback="Ohne Typ" />
                      <TruncCell text={r.bisherigerBearbeiter} fallback="nicht zugewiesen" />
                      <TableCell>
                        <Tooltip title={r.currentStatus && r.currentStatus !== r.status ? `Bei Auswahl: ${VORGANG_STATUS_LABEL[r.status]}` : ''}>
                          <span>
                            <StatusBadge status={r.currentStatus ?? r.status} wrap />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <ScoreCell row={r} field="correctness" />
                      </TableCell>
                      <TableCell align="right">
                        <ScoreCell row={r} field="errorRisk" />
                      </TableCell>
                      <TableCell>
                        <ResultReason row={r} />
                        {r.claim.active && !r.claim.mine && (
                          <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                            In manueller Prüfung
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </QueryBoundary>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, px: 2, py: 1.5, flexWrap: 'wrap', borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" component="label" htmlFor="rows-per-page" sx={{ color: 'text.secondary' }}>
            Zeilen pro Seite
          </Typography>
          <Select id="rows-per-page" size="small" value={limit} onChange={(e) => setLimit(Number(e.target.value))} inputProps={{ 'aria-label': 'Zeilen pro Seite' }}>
            {[25, 50, 100, 200].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary' }} aria-live="polite">
          {rows.length ? `Einträge ${from}–${from + rows.length - 1}` : 'Keine Einträge'} · Seite {page + 1}
        </Typography>
        <Box>
          <Button size="small" startIcon={<ChevronLeft size={16} />} disabled={page === 0} onClick={() => setCursors((c) => c.slice(0, -1))}>
            Zurück
          </Button>
          <Button
            size="small"
            endIcon={<ChevronRight size={16} />}
            disabled={!q.data?.nextCursor}
            onClick={() => setCursors((c) => [...c, q.data!.nextCursor!])}
          >
            Weiter
          </Button>
        </Box>
      </Box>
    </Card>
  );
}
